import { QueryClient } from "@tanstack/react-query";

// Increment this after any data schema migration to force-clear
// the persisted React Query cache for all users.
// 7: GptConversation messages reshaped to the parts[] transcript contract —
//    persisted pre-migration conversation payloads (fromUser/content shape)
//    crash the new renderers (message.parts is undefined).
// 8: Chat-with-Paper moved to the Vercel AI SDK runtime — the /history endpoint
//    now returns { messages } (was { history, conversation_id }); a stale
//    persisted payload would crash the renderers (body.messages is undefined).
// 9: General Juisci GPT chat moved to the Vercel AI SDK runtime (brique A) —
//    the conversation page now seeds useChat from /conversations/:id and the
//    source components render the minimal GptSourceRef shape.
// 10: Article cards swapped the `aiGeneratedImage: { url }` wire field for
//    `cardImage: { url, sizes } | null` — ArticlePublicationCard now reads
//    cardImage.sizes, so a stale persisted payload (no cardImage) crashes the
//    card with "undefined is not an object (evaluating 'cardImage.sizes')"
//    (JUISCI-APP-2CF).
// 11: Article publication facts (authors, journal, date, type, keywords,
//    access, tags, specialties) now source from the canonical Work resolved
//    layer instead of per-language Article columns (PR C2 read-flip). Wire
//    shape is byte-identical, but sibling-language cards can now show corrected
//    (de-drifted) values — bump so stale persisted lists refetch.
// 12: `/users/me` gained `preferenceOnboarding`; stale persisted bodies lack it
//    and crashed DiscoveryPreferenceOnboarding on hydration. Bump to drop them.
// 13: Visual Abstract v3 replaced `callout`/`visual` with `callouts[]`/`visuals[]`.
//    Persisted private-detail payloads from v2 crash HeroBento on hydration.
// 14: Congress card payloads swapped `coverImageUrl`/`logoUrl` for the `cover`
//     discriminated union; a stale persisted body has no `cover`, so the hero
//     crashed on hydration with "Cannot read properties of undefined (reading
//     'type')" in CongressCard.
// 15: Congress space payloads gained `topSessionsCardTitle` (admin-authored,
//     per content language). A stale persisted body has it `undefined`, which
//     passes the `!== null` authored check and renders an empty heading on the
//     Top Sessions card and page.
// 16: The visual abstract view replaced `heroMessage: string` with `hero`, an
//     array of emphasis segments, and panels gained `icon`. A persisted
//     pre-change private-detail body has `hero` undefined, crashing `HeroBento`
//     on `view.hero.map` — the same failure mode v3 already caused on
//     `view.callouts[0]`.
// 17: Congress space content payloads gained `headerTitle` on the
//     `thematicCollection` block (the collection card's overline). A stale
//     persisted body has it `undefined`, which passes PlaylistCard's
//     `!== null` check and then CRASHES the congress-space page on
//     `headerTitle.trim()`. Strictly worse than 15, which was the same
//     undefined-passes-the-null-check shape on the same endpoint but only
//     rendered an empty heading. Bump to drop those bodies.
// 18: `/users/me/interests` gained `learnedTopics` (the UPP topics head, offered
//     as suggestions on the topics turn). A stale persisted body has it
//     `undefined`, so ProfilingConversation crashes on `learned.map` when a
//     returning user reopens "Customize your feed" — same hydration shape as 12
//     and 16. Bump to drop those bodies.
// 19: Congress space content `pdfCard` blocks gained `documentKind` (a document
//     is now a PDF or an image). A stale persisted body has it `undefined`, which
//     reaches the exhaustive switch in CongressSpaceContent and CRASHES the
//     Resources tab on the `satisfies never` throw before the background refetch
//     can replace the body.
//     The guard is deliberately NOT softened to treat an absent kind as `pdf`,
//     even though that is the column default and semantically right for old rows:
//     the throw exists for the THIRD kind. If a newer server sends a kind this
//     build does not know, we want a loud failure, not a silent render as a PDF.
//     Bumping closes the stale-cache window completely, so the guard keeps doing
//     the job it is there for.
// 20: The visual-abstract library gained `branding` (the poster's own colour and
//     mark) so a library card can be the poster's header band instead of a text
//     tile. A body persisted by the text-tile build has it `undefined`, and the
//     card CRASHES on `branding.primaryColor` before the background refetch can
//     replace it — the same hydration shape as 14 and 16.
// 21: The Evidence GPT history drawer's conversation feeds moved from a regular
//     `useApiQuery` to `useApiInfiniteQuery` (paginated). react-query stores the
//     two hooks' data in INCOMPATIBLE shapes — regular `{ body, status }` vs
//     infinite `{ pages, pageParams }` — and they had shared a cache key, so a
//     persisted regular entry (e.g. `["api","/webapp/private/evidence-gpt/
//     conversations",{},{}]`, dataKeys `body,status`, no `pages`) rehydrated
//     under the infinite hook made react-query dereference
//     `data.pages[data.pages.length - 1]` on `undefined` and CRASH the drawer on
//     mount ("undefined is not an object (evaluating 't.length')", useBaseQuery
//     → EvidenceHistoryDrawer). The durable fix namespaces infinite-query keys
//     with a trailing `"infinite"` marker so the shapes can never share a slot;
//     this bump also purges the already-persisted pre-namespace entries.
// v22: `/webapp/private/rooms/:id` gained `filterTopicModels` (topic-based room
//     filter). A body persisted before that deploy lacks the field, and
//     RoomDiscoveryPage reads `filterTopicModels.length` unconditionally — the
//     stale shape would crash the room page until cleared, so purge on deploy.
// v23: the shared Article DTO gained `evidenceLevel` and `journalRank`
//     (with `journalRank.bands`), read unguarded by the article-page works
//     score. A detail body persisted before that deploy lacks them and would
//     crash the article page, so purge on deploy.
// v24: `evidenceLevel.domains[]` gained `signals` (the expandable signalling
//     questions), read unguarded (`domain.signals.length`) by the evidence
//     sheet. A detail body persisted before that deploy crashes the article
//     page, so purge on deploy.
// v26: `evidenceLevel` became `appraisal`, projected to the appraised state
// alone. A persisted v25 body carries the old field, and the works score reads
// the new one.
// v25: `evidenceLevel` design_only gained `design` + `note` (abstract-only
//     verdicts). A stale body's `design` is undefined, which passes the
//     `!== null` check and crashes `designLabel`'s exhaustive switch, so
//     purge on deploy.
// v27: `hcpIdentifier` became `hcpRegistryIds` (#1688), read unguarded by
//     `useMixpanelUser` (`hcpRegistryIds.length`) and by the room HCP gate. A
//     persisted v26 user has no such field, so the boot that rehydrates it
//     throws before the query refetches. This shipped to production and hit
//     real users — the field is REQUIRED in the /users/me contract, so only a
//     stale cache can make it undefined.
// v28: `/webapp/public/conversations/shared/:token` gained `evidenceAnswers`,
//     iterated unguarded by GPTSharedConversationPage. A shared conversation
//     persisted before that deploy has no such field, and reopening the link
//     throws "evidenceAnswers is not iterable" before the refetch lands.
// v29: every Search turn of the private and shared conversation hydrates
//     gained `pubmedDocs`, read unguarded by GptSearchTurn (`pubmedDocs.length`).
//     A conversation persisted before that deploy has no such field, and
//     reopening it throws before the refetch lands.
// v30: `/webapp/private/users/me` gained `professionalRecordStatus`.
//     A persisted v29 user lacks it, so the Room prompt could stay closed until
//     the cached user refetches.
// v31: every populated playlist gained `featuredArticle`, read unguarded by
//     DesktopCollectionCard (`featuredArticle.journalName`). A playlist list
//     persisted before that deploy (feed, saved, shared, room resources) has
//     no such field, and the desktop card throws before the refetch lands.
// v32: the room sections route returns `{ entries }` (sections AND congress
//     spaces in the room's one order) instead of `{ sections }`. A persisted
//     pre-deploy body has no `entries`, and the Resources page maps over it
//     before the refetch lands.
// v33: `/webapp/private/rooms/:id` gained `partnerContact`, read by an
//     exhaustive switch in the room sheets, and `/webapp/private/users/me`
//     gained `partnerObjectionInformed`, read by the reconnection redirect
//     (docs/partner-objection.md). A persisted v32 body lacks both.
// v34: `/webapp/private/conversations/:id` gained `continuedAs`, read by the
//     conversation page's redirect. A persisted v33 body lacks it, and the
//     redirect would leave for `/gpt/chat/conversation/undefined`.
//     `/assistant/conversations` gained `sortDate`, which the history sorts
//     and dates its rows by: a v33 body would give an Invalid Date.
// v35: `/webapp/private/feed` no longer contains playlists. Drop persisted
//     mixed feeds before the publication-only renderers and analytics read them.
// v36: `/assistant/conversations/:conversationId` messages gained
//     `metadata.stakes`, read by an exhaustive switch that names the panel's
//     Mode: a v35 body lacks it and the switch throws during render. The
//     conversation's references gained `journal` and `year`, which the
//     "About this study" card prints.
// v37: `/webapp/private/saved` gained `listArticleIds`, read unguarded by the
//     desktop save button (`listArticleIds.includes`). A persisted v36 body
//     lacks it, and every article row of the feed throws before the refetch
//     lands.
// v38: the collection blocks of `/webapp/private/congress-spaces/:id/content`
//     gained `featuredArticle` and `journalCount`, read unguarded by
//     DesktopCollectionCard (`featuredArticle.journalName`) on the desktop
//     congress page. A persisted v37 body lacks both, and the page throws
//     before the refetch lands.
// v39: `/webapp/private/users/me/partner-sharing` partners gained
//     `privacyPolicies`, read unguarded by the desktop Privacy policy ›
//     Partners tab (`privacyPolicies.length`). A persisted v38 body lacks it,
//     and the tab throws before the refetch lands.
// v40: the article payloads (detail, feed and list items) gained
//     `offLabelWarnings`, read unguarded by ArticleSectionOffLabel
//     (`offLabelWarnings.length`). A persisted v39 body lacks it, and the
//     article page throws before the refetch lands.
// v41: the assistant share of `/webapp/public/conversations/shared/:token`
//     gained `messages`, read unguarded by the share page
//     (`assistantUIMessages(conversation.messages)`). A persisted v40 body
//     lacks it, and the page throws before the refetch lands. The conversation
//     list and detail routes and the Chat-with-Paper history gained `isShared`.
// v42: the content items of `/webapp/public/rooms/:roomId/resources` gained
//     `episodeOf`, read by the mobile Resources audio card
//     (`roomAudioSource`). A persisted v41 body lacks it: the card would name
//     the episode's journal instead of its series' owner until the refetch
//     lands (`roomAudioSource` reads a missing field as "not a stand-in", so
//     it no longer throws).
// v43: the room payloads (detail, lists, stats, invite preview) replaced
//     `articleCount` and `playlists` with `contentCounts`, read unguarded by
//     the room header, the rooms lists and the welcome sheet
//     (`contentCounts[contentLang]`). A persisted v42 body lacks it, and the
//     room page throws before the refetch lands.
// v44: `/users/me` gained `experimentalFeatures`, which the settings read to
//     show the staff switch. A persisted v43 body lacks it, and the switch
//     would show to a user who is not staff.
// v45: `journalRank` lost `band` and `bands` for `fieldBand`, the band in the
//     journal's research field. The article page and the source sheets read
//     `journalRank.fieldBand` unguarded, so a persisted v44 body would crash
//     them: purge on deploy.
export const QUERY_CACHE_VERSION = "45";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: Infinity, // Disable garbage collection
      staleTime: 1000,
      retry: (failureCount, error) => {
        // Retry on network errors
        if (error.message.toLowerCase().includes("network error")) {
          return true;
        }
        return failureCount < 3;
      },
    },
  },
});
